import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Code,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconEye, IconHistory } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useAudit } from './auditApi';
import type { AuditFilter, AuditLogDto } from './types';

const PAGE_SIZE = 50;

// Arabic labels for the entity CLR type names the interceptor records.
const ENTITY_LABEL_AR: Record<string, string> = {
  Item: 'صنف',
  Ingredient: 'مكوّن',
  Category: 'قسم',
  ModifierGroup: 'مجموعة إضافات',
  Modifier: 'إضافة',
  Tax: 'ضريبة',
  TaxGroup: 'مجموعة ضرائب',
  PaymentMethod: 'طريقة دفع',
  Promotion: 'عرض',
  User: 'مستخدم',
  Role: 'دور',
  Branch: 'فرع',
  Customer: 'عميل',
  Supplier: 'مورّد',
  Account: 'حساب',
  DeliveryZone: 'منطقة توصيل',
};

const dateFmt = new Intl.DateTimeFormat('ar-PS', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Jerusalem',
  numberingSystem: 'latn',
});

function ActionBadge({ action }: { action: number }) {
  const { t } = useTranslation();
  const map: Record<number, { color: string; key: string }> = {
    1: { color: 'green', key: 'audit.action.create' },
    2: { color: 'blue', key: 'audit.action.update' },
    3: { color: 'red', key: 'audit.action.delete' },
    4: { color: 'gray', key: 'audit.action.view' },
  };
  const cfg = map[action] ?? { color: 'gray', key: 'audit.action.view' };
  return (
    <Badge color={cfg.color} variant="light" radius="sm">
      {t(cfg.key)}
    </Badge>
  );
}

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [action, setAction] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<AuditLogDto | null>(null);

  const filter: AuditFilter = useMemo(
    () => ({
      from: from || undefined,
      to: to || undefined,
      action: action ? Number(action) : undefined,
      entityType: entityType || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [from, to, action, entityType, page],
  );

  const { data, isLoading } = useAudit(filter);

  const entityLabel = (type: string) =>
    i18n.language === 'ar' ? ENTITY_LABEL_AR[type] ?? type : type;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const rows = data?.items ?? [];

  const resetToFirstPage = () => setPage(1);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.settings')}
        title={t('audit.page.title')}
        subtitle={t('audit.page.subtitle')}
      />

      <Paper p="md">
        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput
            type="date"
            label={t('audit.filter.from')}
            value={from}
            onChange={(e) => { setFrom(e.currentTarget.value); resetToFirstPage(); }}
          />
          <TextInput
            type="date"
            label={t('audit.filter.to')}
            value={to}
            onChange={(e) => { setTo(e.currentTarget.value); resetToFirstPage(); }}
          />
          <Select
            label={t('audit.filter.action')}
            placeholder={t('audit.filter.all')}
            clearable
            value={action}
            onChange={(v) => { setAction(v); resetToFirstPage(); }}
            data={[
              { value: '1', label: t('audit.action.create') },
              { value: '2', label: t('audit.action.update') },
              { value: '3', label: t('audit.action.delete') },
              { value: '4', label: t('audit.action.view') },
            ]}
            w={160}
          />
          <Select
            label={t('audit.filter.entity')}
            placeholder={t('audit.filter.all')}
            clearable
            searchable
            value={entityType}
            onChange={(v) => { setEntityType(v); resetToFirstPage(); }}
            data={Object.keys(ENTITY_LABEL_AR).map((k) => ({ value: k, label: entityLabel(k) }))}
            w={200}
          />
        </Group>
      </Paper>

      <Paper p={rows.length ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}><Loader size="sm" /></Stack>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<IconHistory size={22} stroke={1.6} />}
            title={t('audit.empty.title')}
            description={t('audit.empty.body')}
          />
        ) : (
          <Table verticalSpacing="sm" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('audit.columns.time')}</Table.Th>
                <Table.Th>{t('audit.columns.actor')}</Table.Th>
                <Table.Th>{t('audit.columns.action')}</Table.Th>
                <Table.Th>{t('audit.columns.entity')}</Table.Th>
                <Table.Th>{t('audit.columns.record')}</Table.Th>
                <Table.Th style={{ width: 90, textAlign: 'end' }}>{t('audit.columns.details')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.auditLogId}>
                  <Table.Td className="font-numeric" style={{ whiteSpace: 'nowrap' }}>
                    <Text size="sm">{dateFmt.format(new Date(r.occurredAt))}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={r.actorName ? 'var(--wheels-ink-strong)' : 'dimmed'}>
                      {r.actorName ?? t('audit.system')}
                    </Text>
                  </Table.Td>
                  <Table.Td><ActionBadge action={r.action} /></Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{entityLabel(r.entityType)}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{r.entityLabel ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'end' }}>
                    {r.details ? (
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        leftSection={<IconEye size={14} stroke={1.6} />}
                        onClick={() => setDetail(r)}
                      >
                        {t('audit.columns.details')}
                      </Button>
                    ) : (
                      <Text size="sm" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {data && data.total > 0 && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed" className="font-numeric">
            {t('audit.pageInfo', { page: data.page, totalPages, total: data.total })}
          </Text>
          <Group gap="xs">
            <Button
              variant="default"
              size="compact-sm"
              leftSection={<IconChevronRight size={14} stroke={1.6} />}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t('audit.prev')}
            </Button>
            <Button
              variant="default"
              size="compact-sm"
              rightSection={<IconChevronLeft size={14} stroke={1.6} />}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('audit.next')}
            </Button>
          </Group>
        </Group>
      )}

      <Modal
        opened={detail !== null}
        onClose={() => setDetail(null)}
        title={t('audit.detailTitle')}
        size="lg"
        centered
      >
        {detail && (
          <Stack gap="sm">
            <Group gap="xs">
              <ActionBadge action={detail.action} />
              <Text fw={600}>{entityLabel(detail.entityType)}</Text>
              {detail.entityLabel && <Text c="dimmed">· {detail.entityLabel}</Text>}
            </Group>
            <Text size="sm" c="dimmed" className="font-numeric">
              {dateFmt.format(new Date(detail.occurredAt))} · {detail.actorName ?? t('audit.system')}
            </Text>
            <Code block dir="ltr" style={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {prettyJson(detail.details)}
            </Code>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function prettyJson(raw: string | null): string {
  if (!raw) return '—';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
